// Source data for the Severance & Termination Law Atlas. Verified as of 24 Sep 2026.
// Not legal advice; statutes change often.

export type BaselineRule = { h: string; law: string; pts: string[]; but?: string }
export type StateRow = [string, string, StateInput]
export type Rated = [number, string]
export type StateInput = {
  fire: Rated
  quit: string
  sev?: Rated
  warn?: Rated
  pto?: Rated
  sep?: string
  other?: string[]
  disc?: string[]
}
export type Discrepancy = {
  id: string
  sev: 'high' | 'med' | 'low'
  t: string
  states: string[]
  c: [string, string | null][]
  v: string
}

export const BASE: BaselineRule[] = [
  {
    h: 'Severance is voluntary, not a legal entitlement',
    law: 'FLSA · US DOL',
    pts: [
      'No federal law requires severance. The US DOL calls it “a matter of agreement between an employer and an employee.”',
      'It becomes enforceable once promised in an offer letter, contract, handbook, collective agreement or plan.',
      'A formal, ongoing severance program can be an ERISA welfare plan, which brings a written plan document, a summary plan description (SPD) and a claims procedure.',
    ],
    but: 'State exceptions: New Jersey, Maine and Hawaii require payments in closings or mass layoffs. See the exceptions section.',
  },
  {
    h: 'Federal WARN notice for large layoffs',
    law: '29 U.S.C. §2101 et seq.',
    pts: [
      'Covers employers with 100+ full-time employees.',
      '60 calendar days’ written notice before a plant closing (50+ workers at one site) or a mass layoff (50–499 workers who are at least 33% of the site, or 500+).',
      'A violation costs up to 60 days of back pay and benefits per worker, plus civil penalties for failing to notify local government.',
      'Exceptions: faltering company, unforeseeable business circumstances, natural disaster.',
    ],
    but: 'Sixteen states have their own mandatory “mini-WARN” laws with lower thresholds or longer notice periods.',
  },
  {
    h: 'Releases from workers 40 and older',
    law: 'ADEA as amended by OWBPA',
    pts: [
      'The release must be written in plain language and refer to the ADEA by name.',
      'The employee must get 21 days to consider it (45 days in a group program) and 7 days to revoke after signing.',
      'The employer must advise the employee in writing to consult an attorney.',
      'It must be supported by consideration beyond anything already owed.',
      'Group programs must disclose the decisional unit and the ages and job titles of those selected and not selected.',
    ],
    but: 'California, Illinois, Minnesota, New York and New Jersey add their own rules. New York has a bill pending that would extend the 21/7-day periods to all ages.',
  },
  {
    h: 'Rights a release cannot waive',
    law: 'EEOC · SEC 21F-17 · NLRA · Speak Out Act',
    pts: [
      'Filing a charge with the EEOC or a state agency, or taking part in an investigation.',
      'Whistleblowing to the SEC or other regulators.',
      'Pre-dispute NDAs about sexual assault or harassment are unenforceable (Speak Out Act, 2022).',
      'Under the NLRB’s McLaren Macomb decision (2023), broad confidentiality and non-disparagement clauses offered to non-supervisory employees can violate the NLRA. The General Counsel’s guidance memo was rescinded in 2025, but the Board decision hasn’t been overruled and the NLRB applied it again in 2026 (Valley Radiology).',
      'Unpaid FLSA wages generally can’t be released privately without DOL or court supervision.',
    ],
  },
  {
    h: 'Health coverage continuation',
    law: 'COBRA · 29 U.S.C. §1161',
    pts: [
      'Applies to group health plans of employers with 20+ employees.',
      'Coverage lasts up to 18 months after termination (up to 36 for some qualifying events).',
      'The employee can be charged up to 102% of the premium. Many severance packages subsidize this.',
    ],
    but: 'Most states have “mini-COBRA” laws for smaller employers, and Connecticut and Massachusetts require employer-paid continuation in closings.',
  },
  {
    h: 'Severance is taxed as wages',
    law: 'IRS supplemental wages · IRC §409A',
    pts: [
      'Subject to federal income tax withholding, Social Security, Medicare and FUTA, and reported on the W-2.',
      'Optional flat federal withholding of 22% up to $1M in supplemental wages, and mandatory 37% above that.',
      'Installment or deferred severance must comply with §409A (separation-pay exemption, or a fixed schedule).',
    ],
  },
  {
    h: 'Earned wages and final pay',
    law: 'FLSA + state wage-payment acts',
    pts: [
      'Earned wages are owed whether or not the employee signs anything. Final pay can’t be made conditional on signing a release.',
      'Forty-six states set a deadline for final pay. Alabama, Florida, Georgia and Mississippi have no statute, so the regular payday is the norm.',
      'The most common rule is “next regular payday.” About a dozen states require payment the same day or within 24–72 hours for involuntary terminations.',
    ],
    but: 'Final-pay deadlines are the most varied rule between states. Use the explorer’s “Final pay (fired)” lens.',
  },
  {
    h: 'At-will employment',
    law: 'Common law',
    pts: [
      'Employment is at-will in 49 states. Either side can end it for any lawful reason without notice.',
      'Common exceptions: public policy (e.g., firing for jury duty or whistleblowing), implied contracts in handbooks or promises, and in some states an implied covenant of good faith.',
      'Anti-discrimination (Title VII, ADA, ADEA) and anti-retaliation laws apply everywhere.',
    ],
    but: 'Montana is the exception. After a probationary period, its Wrongful Discharge from Employment Act requires good cause.',
  },
]

export const S: StateRow[] = [
  [
    'AL',
    'Alabama',
    {
      fire: [0, 'No state final-pay statute'],
      quit: 'No state statute',
      sep: 'Notice of Availability of Unemployment Compensation, at separation',
    },
  ],
  [
    'AK',
    'Alaska',
    {
      fire: [2, 'Within 3 working days'],
      quit: 'Next regular payday at least 3 days after notice',
      sep: 'Separation notice within 7 days of last day',
    },
  ],
  [
    'AZ',
    'Arizona',
    {
      fire: [2, 'Within 7 working days or end of next pay period, whichever is sooner'],
      quit: 'Next regular payday',
      sep: 'Form UIB-1015A, at separation',
      disc: ['D7'],
    },
  ],
  [
    'AR',
    'Arkansas',
    {
      fire: [1, 'Next regular payday (double wages if >7 days late)'],
      quit: 'Next regular payday',
      sep: 'UI notice (employer’s delivery method), at separation',
    },
  ],
  [
    'CA',
    'California',
    {
      fire: [3, 'Immediately, at time of discharge'],
      quit: 'Within 72 hours; on last day if 72 hours’ notice given',
      warn: [
        2,
        'Cal-WARN: 75+ employees; 50+ laid off at a site (no 33% test); 60 days. SB 617 added notice content rules, eff. 1 Jan 2026.',
      ],
      pto: [
        3,
        'Required. Vacation is earned wages and “use-it-or-lose-it” is banned. Reasonable accrual caps allowed.',
      ],
      sep: '“For Your Benefit” (DE 2320) + written notice of change in relationship, by termination date',
      other: [
        'Gov. Code §12964.5: agreements with non-disparagement clauses must carve out speech about unlawful acts; the employee must be told they can consult a lawyer and get ≥5 business days to consider.',
        'Civ. Code §1542: a general release doesn’t cover unknown claims unless expressly waived.',
        'Bus. & Prof. Code §16600: noncompetes void, including out-of-state ones.',
        'Waiting-time penalty: up to 30 days’ wages for late final pay (Lab. Code §203).',
      ],
      disc: ['D18'],
    },
  ],
  [
    'CO',
    'Colorado',
    {
      fire: [3, 'Immediately (6h / 24h grace if payroll office closed or offsite)'],
      quit: 'Next regular payday',
      pto: [
        3,
        'Required. Forfeiture of earned vacation is prohibited (Nieto v. Clark’s Market, 2021).',
      ],
      sep: 'Employer Separation Form 22-234, at separation',
      other: [
        'Noncompetes only for employees earning ≥ $130,014 (2026); customer non-solicits ≥ $78,008.40. Thresholds adjust every year.',
      ],
      disc: ['D11'],
    },
  ],
  [
    'CT',
    'Connecticut',
    {
      fire: [3, 'Next business day'],
      quit: 'Next regular payday',
      sev: [
        1,
        'No cash severance mandate, but when a covered establishment relocates or closes, the employer must pay in full for existing group health coverage for 120 days or until the employee gets other group coverage, whichever is shorter (Conn. Gen. Stat. §31-51o).',
      ],
      sep: 'Form UC-61, immediately on termination',
      disc: ['D14'],
    },
  ],
  [
    'DE',
    'Delaware',
    {
      fire: [
        1,
        'Later of next regular payday or 3 business days after last day worked (amended Oct 2022)',
      ],
      quit: 'Later of next regular payday or 3 business days after last day worked',
      disc: ['D21'],
      warn: [
        2,
        'Delaware WARN (19 Del. C. §1901): 100+ employees; closing, mass layoff or relocation of 50+; 60 days. Penalties up to $1,000/day.',
      ],
    },
  ],
  [
    'FL',
    'Florida',
    { fire: [0, 'No state final-pay statute'], quit: 'No state statute', disc: ['D15'] },
  ],
  [
    'GA',
    'Georgia',
    {
      fire: [0, 'No state final-pay statute'],
      quit: 'No state statute',
      sep: 'Form DOL-800, immediately on separation',
      disc: ['D15'],
    },
  ],
  [
    'HI',
    'Hawaii',
    {
      fire: [3, 'Immediately, or next working day'],
      quit: 'Next regular payday; immediately if one pay period’s notice',
      sev: [
        2,
        'Dislocated worker allowance (HRS §394B-10): after a closing, partial closing or relocation, the employer pays each eligible worker the gap between their weekly wage and UI benefits, for up to 4 weeks.',
      ],
      warn: [
        2,
        'Dislocated Workers Act (HRS ch. 394B): 50+ employees; 60 days before closing, partial closing or relocation.',
      ],
      disc: ['D1'],
    },
  ],
  [
    'ID',
    'Idaho',
    {
      fire: [1, 'Next payday or 10 working days, whichever first (48h if written request)'],
      quit: 'Same as involuntary',
    },
  ],
  [
    'IL',
    'Illinois',
    {
      fire: [1, 'No later than next regular payday'],
      quit: 'No later than next regular payday',
      warn: [
        2,
        'Illinois WARN (820 ILCS 65): 75+ employees; mass layoff of 25+ (if 33%) or 250+; 60 days.',
      ],
      pto: [
        3,
        'Required. Earned vacation can’t be forfeited under the Wage Payment & Collection Act.',
      ],
      sep: '“What Every Worker Should Know About Unemployment Insurance”, at separation',
      other: [
        'Workplace Transparency Act: if an agreement makes allegations of unlawful practices confidential, the employee gets 21 days to consider and 7 days to revoke.',
        'Freedom to Work Act: noncompetes void at earnings ≤ $75,000 (rising to $80,000 in 2027); non-solicits void at ≤ $45,000.',
      ],
      disc: ['D16'],
    },
  ],
  [
    'IN',
    'Indiana',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      pto: [2, 'Pay out if promised by policy or agreement. Treated as deferred wages.'],
      other: [
        'Service letter on written request stating whether the employee quit or was discharged (Ind. Code 22-6-3-1); applies to employers that require written applications.',
      ],
    },
  ],
  [
    'IA',
    'Iowa',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      warn: [
        2,
        'Iowa Code ch. 84C: 25+ employees; 25+ affected at a site; 30 days. Lowest employer threshold of any state.',
      ],
      sep: 'Separation notice via employer portal',
    },
  ],
  [
    'KS',
    'Kansas',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      other: [
        'Service letter on written request: tenure, job classification and wage rate (K.S.A. 44-808).',
      ],
    },
  ],
  [
    'KY',
    'Kentucky',
    {
      fire: [1, 'Next payday or 14 days, whichever is later'],
      quit: 'Next payday or 14 days, whichever is later',
    },
  ],
  [
    'LA',
    'Louisiana',
    {
      fire: [1, 'Next payday or 15 days, whichever first'],
      quit: 'Next payday or 15 days, whichever first',
      pto: [3, 'Required. Earned vacation must be paid out.'],
      sep: 'Form LWC-77, within 3 days',
    },
  ],
  [
    'ME',
    'Maine',
    {
      fire: [1, 'Next payday or within 2 weeks of demand, whichever first'],
      quit: 'Same as involuntary',
      sev: [
        2,
        'Mandatory severance on closing or mass layoff (26 M.R.S. §625-B) at a “covered establishment” (100+ employees in the prior 12 months): 1 week’s pay per year of service for employees with 3+ years. Due within one pay period, on top of final wages.',
      ],
      warn: [2, '90 days’ notice of a closing or relocation, under the same statute.'],
      pto: [
        3,
        'Required for employers with 11+ employees (vacation accrued since 1 Jan 2023), regardless of policy, unless a union contract says otherwise (26 M.R.S. §626). Violations bring double liquidated damages.',
      ],
      other: ['Service letter: reason for discharge on written request.'],
      disc: ['D1'],
    },
  ],
  [
    'MD',
    'Maryland',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      warn: [
        2,
        'Economic Stabilization Act: 50+ employees; reduction in operations of the greater of 25% or 15 employees within 3 months; 60 days. Mandatory since 2020–21; enforced since the final regulations of 13 Oct 2025; penalty up to $10,000/day.',
      ],
      pto: [2, 'Required unless a written policy, given at hire, says otherwise.'],
      sep: 'UI notification form, at separation',
      disc: ['D2'],
    },
  ],
  [
    'MA',
    'Massachusetts',
    {
      fire: [3, 'Day of discharge (including accrued vacation)'],
      quit: 'Next regular payday',
      sev: [
        1,
        'No cash severance mandate. The plant-closing law (M.G.L. c.151A §71A–71H) requires continued group health coverage in qualifying closings.',
      ],
      warn: [1, 'Voluntary notice only for plant closings (50+ employees at the facility).'],
      pto: [3, 'Required. Vacation counts as wages under the Wage Act (treble damages).'],
      sep: 'UI claim brochure (Form 2062), at separation',
      other: [
        'Noncompete Act: max 12 months; garden leave of ≥50% of highest base pay (or other agreed consideration); void for non-exempt workers.',
      ],
      disc: ['D5', 'D14'],
    },
  ],
  [
    'MI',
    'Michigan',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      warn: [1, 'Voluntary notice only (MCL §450.731 et seq.).'],
      sep: 'Form UIA-1711, at separation',
      disc: ['D14'],
    },
  ],
  [
    'MN',
    'Minnesota',
    {
      fire: [3, 'Within 24 hours of written demand'],
      quit: 'Next payday (if <5 days away, the following one; max 20 days)',
      warn: [
        1,
        'Voluntary. Notice “as early as possible” for layoffs of 50+ (Minn. Stat. §116L.976).',
      ],
      other: [
        'Releases of Minnesota Human Rights Act claims: 15-day rescission period (Minn. Stat. §363A.31), plus the federal 21/7.',
        'Noncompetes banned for agreements signed on or after 1 Jul 2023.',
        'Service letter: reason for termination on written request.',
      ],
    },
  ],
  ['MS', 'Mississippi', { fire: [0, 'No state final-pay statute'], quit: 'No state statute' }],
  [
    'MO',
    'Missouri',
    {
      fire: [3, 'Day of discharge, on demand (penalty up to 60 days’ wages)'],
      quit: 'No state statute',
      sep: 'Form M-INF-288-5-AI, at separation',
      other: ['Service letter on request (RSMo §290.140).'],
    },
  ],
  [
    'MT',
    'Montana',
    {
      fire: [3, 'Immediately; next payday or 15 days if a written policy says so'],
      quit: 'Next payday or 15 days, whichever first',
      pto: [3, 'Required. Earned vacation is wages.'],
      other: [
        'The only state that is not at-will. After probation, discharge needs good cause (Wrongful Discharge from Employment Act); damages up to 4 years’ wages.',
        'Service letter: written reason for discharge on demand (MCA §39-2-801).',
      ],
      disc: ['D19'],
    },
  ],
  [
    'NE',
    'Nebraska',
    {
      fire: [1, 'Next payday or 2 weeks, whichever sooner'],
      quit: 'Same as involuntary',
      warn: [
        2,
        'Nebraska WARN Act (LB 921, signed 14 Apr 2026, eff. 18 Jul 2026): 100+ employees (part-time excluded from the count); 90 days’ notice of mass layoffs or closings. Penalty up to $100/day; no private right of action.',
      ],
      pto: [3, 'Required. “Use-it-or-lose-it” is banned.'],
    },
  ],
  [
    'NV',
    'Nevada',
    {
      fire: [3, 'Immediately (NRS 608.020)'],
      quit: 'Next payday or 7 days, whichever sooner',
      sep: 'Form NUCS-4139, at separation',
      disc: ['D9'],
    },
  ],
  [
    'NH',
    'New Hampshire',
    {
      fire: [2, 'Within 72 hours'],
      quit: 'Next payday; 72 hours if one pay period’s notice',
      warn: [
        2,
        'RSA 275-F: 100+ employees; closing of 50+ or mass layoff of 25+ (if 33%) or 250+; 60 days. Includes a private right of action.',
      ],
    },
  ],
  [
    'NJ',
    'New Jersey',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      sev: [
        3,
        'Mandatory severance in mass layoffs (Millville Dallas Airmotive Plant Job Loss Notification Act, N.J.S.A. 34:21-1, as amended Apr 2023): employers with 100+ employees (counted nationwide, part-time included) who terminate 50+ in NJ owe 1 week’s pay per full year of service, uncapped, plus 4 more weeks if notice falls short. Paid as a lump sum. The employee can’t waive this severance unless the NJ Labor Commissioner or a court approves.',
      ],
      warn: [2, '90 days’ notice. Mass-layoff count is statewide, not per site.'],
      sep: 'Form BC-10, at separation',
      other: [
        'NJLAD §10:5-12.8: NDAs hiding details of discrimination, retaliation or harassment claims are unenforceable against the employee.',
      ],
      disc: ['D1'],
    },
  ],
  [
    'NM',
    'New Mexico',
    {
      fire: [2, 'Within 5 days (10 days if paid by commission or task)'],
      quit: 'Next regular payday',
    },
  ],
  [
    'NY',
    'New York',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      warn: [
        2,
        'NY WARN (Lab. Law §860): 50+ employees; closing of 25+ or mass layoff of 25+ (if 33%) or 250+; 90 days.',
      ],
      pto: [2, 'Required unless a written policy says forfeited.'],
      sep: 'Record of Employment (Form IA 12.3), at separation',
      other: [
        'Labor Law §195(6): written notice of termination date and benefit cancellation within 5 working days.',
        'GOL §5-336: if the complainant prefers confidentiality in a discrimination settlement, they get 21 days to consider and 7 to revoke.',
        'Pending: the “No Severance Ultimatums Act” (S372A/A6480A) passed both houses in June 2026 and awaits the governor. It would require 21/7 days and attorney notice for all severance agreements; non-compliant agreements would be void.',
      ],
      disc: ['D13'],
    },
  ],
  [
    'NC',
    'North Carolina',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      pto: [2, 'Required unless a written policy notified in advance says forfeited.'],
    },
  ],
  [
    'ND',
    'North Dakota',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      pto: [
        3,
        'Required; no policy may forfeit earned PTO. Narrow exception: on a voluntary quit by an employee with <1 year of service who gave <5 days’ notice, if the limit was disclosed in writing at hire (N.D.C.C. §34-14-09.2).',
      ],
      other: ['Noncompetes void (N.D.C.C. §9-08-06).'],
    },
  ],
  [
    'OH',
    'Ohio',
    {
      fire: [1, 'Next payday or within 15 days, whichever is earlier (R.C. §4113.15)'],
      quit: 'Next payday or within 15 days, whichever is earlier',
      pto: [
        2,
        'No statute. Courts treat earned vacation as owed unless a clear written policy says it is forfeited.',
      ],
      warn: [
        2,
        'New mini-WARN (R.C. §4113.31), eff. 30 Sep 2025: mirrors federal thresholds (100+ employees, 50+ at a site); 60 days, with notice to the state.',
      ],
      disc: ['D3', 'D4', 'D22'],
    },
  ],
  [
    'OK',
    'Oklahoma',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      other: ['Noncompetes generally unenforceable (15 O.S. §219A).'],
    },
  ],
  [
    'OR',
    'Oregon',
    {
      fire: [3, 'End of first business day after termination'],
      quit: 'Immediately with 48h notice; otherwise 5 business days or next payday, whichever first',
      warn: [
        1,
        'Mirrors federal WARN (ORS 285A.510); requires notice to the state. No separate state penalty.',
      ],
    },
  ],
  [
    'PA',
    'Pennsylvania',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      sep: 'Form UC-1609, at separation',
    },
  ],
  [
    'RI',
    'Rhode Island',
    {
      fire: [1, 'Next regular payday (24h on merger, relocation or liquidation)'],
      quit: 'Next regular payday',
      pto: [
        3,
        'Required after 1 year of service, and the employer can’t attach conditions such as notice requirements (R.I. Gen. Laws §28-14-4).',
      ],
      sep: 'UI notice, at separation',
    },
  ],
  [
    'SC',
    'South Carolina',
    {
      fire: [1, 'Within 48 hours or next payday (max 30 days)'],
      quit: 'Within 48 hours or next payday (max 30 days)',
      sep: 'Notice of Availability of UI Benefits, at separation',
      other: [
        'The old shutdown-notice parity rule (S.C. Code §41-1-40) was repealed in 2010. Some older guides still cite it.',
      ],
    },
  ],
  [
    'SD',
    'South Dakota',
    { fire: [1, 'Next regular payday (or when property returned)'], quit: 'Same as involuntary' },
  ],
  [
    'TN',
    'Tennessee',
    {
      fire: [1, 'Next payday or 21 days, whichever is later'],
      quit: 'Same as involuntary',
      warn: [
        2,
        'Plant Closing & Reduction in Operations Act: covers only employers with 50–99 employees (the size federal WARN skips); 50+ affected over 3 months. No fixed notice period or penalty.',
      ],
      sep: 'Form LB-0489, within 24 hours',
    },
  ],
  [
    'TX',
    'Texas',
    { fire: [2, 'Within 6 calendar days'], quit: 'Next regular payday', disc: ['D6'] },
  ],
  ['UT', 'Utah', { fire: [3, 'Within 24 hours'], quit: 'Next regular payday', disc: ['D8'] }],
  [
    'VT',
    'Vermont',
    {
      fire: [2, 'Within 72 hours'],
      quit: 'Next payday (or the following Friday)',
      warn: [
        2,
        'Notice of Potential Layoffs Act: 50+ employees; 50+ affected in 90 days; 45 days’ notice to the state, 30 days to workers.',
      ],
    },
  ],
  ['VA', 'Virginia', { fire: [1, 'Next regular payday'], quit: 'Next regular payday' }],
  [
    'WA',
    'Washington',
    {
      fire: [1, 'End of the established pay period'],
      quit: 'End of the established pay period',
      warn: [
        2,
        'Securing Timely Notification & Benefits Act (RCW 49.45), eff. 27 Jul 2025: 50+ employees; 50+ affected (no 33% test); 60 days.',
      ],
      other: [
        'Noncompetes only above $126,858.83 (employees, 2026). A near-total ban takes effect 30 Jun 2027 (ESHB 1155, signed 23 Mar 2026).',
      ],
      disc: ['D12'],
    },
  ],
  [
    'WV',
    'West Virginia',
    {
      fire: [1, 'Next regular payday'],
      quit: 'Next regular payday',
      pto: [2, 'Required unless a written policy says otherwise.'],
    },
  ],
  [
    'WI',
    'Wisconsin',
    {
      fire: [1, 'Next payday (24h on merger, relocation or liquidation)'],
      quit: 'Next regular payday',
      warn: [
        2,
        'Business Closing & Mass Layoff Law (Wis. Stat. §109.07): 50+ employees; closing of 25+ or layoff of 25%/25+ or 500+; 60 days.',
      ],
    },
  ],
  [
    'WY',
    'Wyoming',
    {
      fire: [2, 'Next payday or 5 working days, whichever first'],
      quit: 'Same as involuntary',
      pto: [
        2,
        'Required unless a clear written policy, acknowledged by the employee, says otherwise.',
      ],
      other: [
        'Noncompetes largely void since 1 Jul 2025, with exceptions for executives, key staff and trade secrets.',
      ],
      disc: ['D10'],
    },
  ],
]

export const DEF: Record<'sev' | 'warn' | 'pto', Rated> = {
  sev: [0, 'No state severance mandate. Severance is contractual only.'],
  warn: [0, 'Federal WARN only.'],
  pto: [0, 'Governed by employer policy or contract. No payout statute.'],
}

const WF = 'Workforce.com'
const SC = 'SeveranceCalc'
const FH = 'Foothold America'

export const D: Discrepancy[] = [
  {
    id: 'D1',
    sev: 'high',
    t: 'How many states require severance?',
    states: ['NJ', 'ME', 'HI'],
    c: [
      [WF, '“Neither federal nor state laws in the United States mandate severance pay.”'],
      [SC, 'Describes New Jersey’s mandate; says nothing about Maine or Hawaii.'],
      [
        FH,
        '“Only New Jersey requires it” and “New Jersey became the first state to require employers to pay severance.”',
      ],
    ],
    v: 'Three states require payments. New Jersey (mass layoffs, since 2023). Maine: 26 M.R.S. §625-B requires 1 week per year of service for 3+ year employees when a 100+ employee establishment closes or has a mass layoff, and it predates NJ. Hawaii: HRS §394B-10 requires a 4-week dislocated worker allowance topping up UI after a closing or relocation. Workforce.com’s statement is simply wrong.',
  },
  {
    id: 'D2',
    sev: 'high',
    t: 'Maryland mini-WARN notice period and threshold',
    states: ['MD'],
    c: [
      [SC, 'Employers with 50+ employees; 90 days’ notice.'],
      [FH, 'Economic Stabilization Act with 60-day notice for 25%+ workforce reductions.'],
      [WF, null],
    ],
    v: '60 days, for employers with 50+ employees, when a reduction affects the greater of 25% or 15 employees. Mandatory since 2020–21 amendments; enforced since final regulations took effect 13 Oct 2025. SeveranceCalc’s 90 days is wrong. Foothold is close but leaves out the 15-employee floor and the 50-employee threshold.',
  },
  {
    id: 'D3',
    sev: 'high',
    t: 'Does Ohio have a mini-WARN law?',
    states: ['OH'],
    c: [
      [FH, '“Mini-WARN: Federal WARN only.”'],
      [SC, null],
      [WF, null],
    ],
    v: 'Yes. Ohio R.C. §4113.31 took effect 30 Sep 2025. It mirrors federal thresholds and adds notice to the state. Foothold’s page (updated Aug 2026) is out of date.',
  },
  {
    id: 'D4',
    sev: 'med',
    t: 'Ohio vacation payout',
    states: ['OH'],
    c: [
      [FH, '“Accrued vacation required.”'],
      [SC, null],
      [WF, null],
    ],
    v: 'Ohio has no statute on vacation payout. Courts generally treat earned vacation as owed when there is no policy, but they enforce a clear written forfeiture policy. So payout is required only when the policy promises it or says nothing.',
  },
  {
    id: 'D5',
    sev: 'high',
    t: 'Massachusetts final pay for fired employees',
    states: ['MA'],
    c: [
      [WF, 'Termination: immediately.'],
      [SC, 'Must be paid on day of termination if fired.'],
      [FH, '“Final Pay: Next payday.”'],
    ],
    v: 'Day of discharge, including accrued vacation (M.G.L. c.149 §148). Late payment brings mandatory treble damages. Foothold’s “next payday” is correct only for resignations.',
  },
  {
    id: 'D6',
    sev: 'med',
    t: 'Texas final pay',
    states: ['TX'],
    c: [
      [WF, 'Termination: within 6 days. Resignation: next scheduled payday.'],
      [SC, 'Groups Texas with “next regular payday” states.'],
      [FH, '“Final Pay: Next payday.”'],
    ],
    v: 'Fired: within 6 calendar days (Tex. Lab. Code §61.014). Quit: next regular payday. Workforce.com is right; the other two apply the resignation rule to everyone.',
  },
  {
    id: 'D7',
    sev: 'med',
    t: 'Arizona final pay for fired employees',
    states: ['AZ'],
    c: [
      [WF, 'Within 7 business days or next payday, whichever sooner.'],
      [SC, '“3 working days or next regular payday.”'],
      [FH, null],
    ],
    v: 'Within 7 working days or the end of the next regular pay period, whichever is sooner (A.R.S. §23-353). The 3-day figure appears to be an older rule; it isn’t current law.',
  },
  {
    id: 'D8',
    sev: 'med',
    t: 'Utah final pay for resignations',
    states: ['UT'],
    c: [
      [WF, 'Voluntary resignation: within 24 hours.'],
      [SC, null],
      [FH, null],
    ],
    v: 'The 24-hour deadline applies only to involuntary terminations. Resigning employees are paid on the next regular payday (Utah Code §34-28-5).',
  },
  {
    id: 'D9',
    sev: 'med',
    t: 'Nevada final pay for fired employees',
    states: ['NV'],
    c: [
      [WF, 'Termination: within 3 days.'],
      [SC, null],
      [FH, null],
    ],
    v: 'Wages are due immediately on discharge (NRS 608.020). Waiting-time penalties then accrue under NRS 608.040. Paycor’s chart repeats the 3-day figure, but it isn’t in the discharge statute.',
  },
  {
    id: 'D10',
    sev: 'low',
    t: 'Wyoming final pay',
    states: ['WY'],
    c: [
      [WF, 'Next scheduled payday (both cases).'],
      [SC, null],
      [FH, null],
    ],
    v: 'Next regular payday or within 5 working days, whichever comes first (W.S. §27-4-104). The 5-day cap is left out.',
  },
  {
    id: 'D11',
    sev: 'med',
    t: 'Colorado noncompete salary threshold',
    states: ['CO'],
    c: [
      [SC, 'Banned under $112,500 “(2024 threshold, adjusted annually).”'],
      [FH, null],
      [WF, null],
    ],
    v: '$112,500 was the 2022 figure. It was $123,750 in 2024 and $127,091 in 2025, and it is $130,014 in 2026. The customer non-solicit threshold is $78,008.40.',
  },
  {
    id: 'D12',
    sev: 'med',
    t: 'Washington noncompete threshold and upcoming ban',
    states: ['WA'],
    c: [
      [SC, 'Requires earnings above $116,593 “as of 2024.”'],
      [FH, null],
      [WF, null],
    ],
    v: '$116,593 was the 2023 figure. The 2026 threshold is $126,858.83 for employees ($317,147.09 for contractors). ESHB 1155, signed 23 Mar 2026, bans nearly all noncompetes from 30 Jun 2027. No source mentions it.',
  },
  {
    id: 'D13',
    sev: 'med',
    t: 'New York severance-agreement bill',
    states: ['NY'],
    c: [
      [
        FH,
        'Proposed “No Severance Ultimatums Act” (S7609B): 21-day consideration, 7-day revocation, attorney notice.',
      ],
      [SC, 'Non-competes “under increased scrutiny”; no mention of the severance bill.'],
      [WF, null],
    ],
    v: 'The bill in the current session is S372A / A6480A. It passed both houses in June 2026 and, as of September 2026, awaits delivery to Governor Hochul. If signed it takes effect immediately, and non-compliant agreements would be void. Foothold’s bill number is from an earlier session and its status is out of date.',
  },
  {
    id: 'D14',
    sev: 'med',
    t: 'Which states have a “state WARN” law?',
    states: ['MI', 'CT', 'MA'],
    c: [
      [
        FH,
        'Lists Michigan (“State WARN with manufacturing focus”), Connecticut and Massachusetts as state-WARN states.',
      ],
      [SC, 'Massachusetts: “No state-specific WARN act.”'],
      [WF, null],
    ],
    v: 'None of the three requires advance notice. Michigan’s is voluntary. Massachusetts has voluntary notice plus mandatory continued health coverage in plant closings. Connecticut has no notice law, but it requires 120 days of employer-paid health coverage on relocation or closure (§31-51o).',
  },
  {
    id: 'D15',
    sev: 'low',
    t: 'Florida and Georgia final pay',
    states: ['FL', 'GA'],
    c: [
      [WF, 'N/A (no requirements).'],
      [SC, 'Florida grouped with “next regular payday” states.'],
      [FH, 'Final pay: next payday (both).'],
    ],
    v: 'Neither state has a final-pay statute. Paying on the regular payday is common practice under the FLSA, but no state rule requires it. Workforce.com is correct.',
  },
  {
    id: 'D16',
    sev: 'low',
    t: 'Illinois final pay',
    states: ['IL'],
    c: [
      [SC, '“Due by next regular payday or within reasonable time.”'],
      [WF, 'Next scheduled payday.'],
      [FH, 'Next payday.'],
    ],
    v: 'No later than the next regular payday (820 ILCS 115/5). “Within a reasonable time” isn’t in the statute.',
  },
  {
    id: 'D17',
    sev: 'low',
    t: 'How many states have mini-WARN laws?',
    states: [],
    c: [
      [SC, 'Names about 5 (CA, NY, NJ, IL, WI, MD, TN).'],
      [FH, 'Names 7 as “State WARN” (incl. MI, CT, MA).'],
      [
        'Secondary sources',
        'National Law Review: 13. Onwards HR: 18 jurisdictions. LayoffData: 20.',
      ],
    ],
    v: 'The count depends on whether voluntary laws are included and how recent the source is. This atlas counts 16 mandatory laws (CA, DE, HI, IL, IA, ME, MD, NE, NH, NJ, NY, OH, TN, VT, WA, WI) plus 4 voluntary or limited ones (MA, MI, MN, OR). South Carolina’s old shutdown-notice rule, which some guides still list, was repealed in 2010. Recent additions: OH (2025), WA (2025), MD enforcement (2025), NE (2026).',
  },
  {
    id: 'D18',
    sev: 'low',
    t: 'California consideration period',
    states: ['CA'],
    c: [
      [FH, '“5-day minimum for reviewing severance agreements.”'],
      [SC, 'Mentions only Civ. Code §1542 release protection.'],
      [WF, null],
    ],
    v: 'Gov. Code §12964.5 requires notice of the right to consult an attorney and at least 5 business days to consider. The employee may sign sooner. It applies to agreements with non-disparagement or confidentiality terms about workplace conduct.',
  },
  {
    id: 'D19',
    sev: 'low',
    t: 'Montana final pay',
    states: ['MT'],
    c: [
      [SC, '“Same day as termination.”'],
      [
        WF,
        'Immediately (within 4 hours or end of business day), or next payday / 15 days under a written policy.',
      ],
      [FH, null],
    ],
    v: 'Workforce.com has the full rule. The immediate-payment default can be extended by a written policy, so SeveranceCalc overstates it.',
  },
  {
    id: 'D21',
    sev: 'med',
    t: 'Delaware final pay',
    states: ['DE'],
    c: [
      [WF, 'Next scheduled payday (both cases).'],
      [SC, null],
      [FH, null],
    ],
    v: 'Since the October 2022 amendment to 19 Del. C. §1103, final wages are due on the later of the next regular payday or 3 business days after the last day worked. Late payment brings liquidated damages of 10% of unpaid wages per day. Workforce.com’s rule predates the amendment.',
  },
  {
    id: 'D22',
    sev: 'low',
    t: 'Ohio final pay',
    states: ['OH'],
    c: [
      [WF, 'Next scheduled payday.'],
      [SC, 'Groups Ohio with “next regular payday” states.'],
      [FH, '“Final Pay: Next payday.”'],
    ],
    v: 'R.C. §4113.15: final pay is due on the next regular payday or within 15 days, whichever is earlier. All three sources leave out the 15-day cap.',
  },
  {
    id: 'D23',
    sev: 'med',
    t: 'State continuation (mini-COBRA): which states have no law',
    states: ['MI', 'WA', 'ID', 'NV', 'MA', 'HI'],
    c: [
      [
        'CobraHelp (aggregator)',
        'Lists continuation laws for MI (9 mo), WA (6 mo), ID (3 mo), NV (12 mo), HI (18 mo), and MA at 12 months.',
      ],
      [
        'FindCoverage (aggregator)',
        'No general law in AL, AK, ID, MI, NV (repealed 2013), WA; HI limited to sickness; MA 18/29/36 months.',
      ],
      [
        'State agencies / statutes',
        'Mass.gov confirms MA’s 18/36 months. HRS §393-15 covers only sickness. WA has no law for employers under 20.',
      ],
    ],
    v: 'AL, AK, ID, MI, NV and WA have no general continuation law. Hawaii’s only covers illness, not layoffs. Massachusetts mirrors federal durations. CobraHelp is wrong on all six of those states.',
  },
  {
    id: 'D24',
    sev: 'med',
    t: 'State continuation durations: aggregator errors',
    states: ['AZ', 'KY', 'MO', 'SD', 'IN', 'VT', 'WY', 'TX', 'MN'],
    c: [
      [
        'CobraHelp',
        'AZ 6 mo · KY 9 · MO 9 · SD 12 · IN 3 · VT 6 · WY none · TX 9-15 · MN <20 employees only',
      ],
      [
        'FindCoverage',
        'AZ 18 · KY 18 · MO 18/36 · SD 18 · IN 12 · VT 18 · WY 12 · TX 9 + 6 after COBRA · MN any size',
      ],
      [
        'Statutes checked',
        'A.R.S. §20-2330 (18) · KRS 304.18-110 (18) · RSMo §376.428 (mirrors COBRA) · SDCL §58-18-7.5 (18) · Ind. Code §27-8-15-31.1 (12) · W.S. §26-19-113 (12)',
      ],
    ],
    v: 'Where checked against statutes, FindCoverage was right each time and CobraHelp understated the duration. The health coverage table uses the verified figures. Montana remains unresolved and is labeled "Conflict."',
  },
  {
    id: 'D25',
    sev: 'med',
    t: 'Does severance affect unemployment? Four states in dispute',
    states: ['IL', 'VA', 'GA', 'MT'],
    c: [
      ['SeveranceCalc blog', 'IL: offset · VA: no offset · GA: no offset · MT: no offset'],
      [
        'OkayNext guide',
        'IL: no effect · VA: deducted · GA: disqualifies weeks where severance exceeds the benefit · MT: may reduce the separation week only',
      ],
      [
        'State sources',
        'IDES / IL counsel: severance isn’t wages. VEC: severance is deducted once reported. GA DOL: ineligible while weekly severance exceeds the benefit. MT: may reduce the separation week.',
      ],
    ],
    v: 'OkayNext is correct on all four. SeveranceCalc, from the same site as one of the original source articles, reverses Illinois and Virginia, which changes a laid-off worker’s benefits in both states.',
  },
  {
    id: 'D20',
    sev: 'low',
    t: 'COBRA duration and scope',
    states: [],
    c: [
      [SC, '“Up to 18 months post-termination.”'],
      [FH, 'Mentioned, no specifics.'],
      [WF, null],
    ],
    v: '18 months for termination, but up to 36 months for some qualifying events. COBRA applies only to employers with 20+ employees; smaller employers fall under state mini-COBRA laws.',
  },
]
